import React, { useEffect, useState } from "react";
import Header from "./header";
import SideBar from "./SideBar";
import { GlobalService } from "./service/GlobalService";
import axios from "axios";

export const DashboardStructure = () => {
  // Stock data states
  const [tableData, setTableData] = useState([]);
  const [tableData1, setTableData1] = useState([]);
  const [tableData2, setTableData2] = useState([]);

  // Statistics states
  const [totalSales, setTotalSales] = useState(0);
  const [totalPurchases, setTotalPurchases] = useState(0);
  const [totalCustomers, setTotalCustomers] = useState(0);
  const [totalSuppliers, setTotalSuppliers] = useState(0);
  const [totalPendingAmount, setTotalPendingAmount] = useState(0);
  const [todaySales, setTodaySales] = useState(0);
  const [todayPurchases, setTodayPurchases] = useState(0);
  const [monthSales, setMonthSales] = useState(0);
  const [monthPurchases, setMonthPurchases] = useState(0);

  // Recent invoices state
  const [recentInvoices, setRecentInvoices] = useState([]);

  // Loading states
  const [loading, setLoading] = useState(true);
  const [stockLoading, setStockLoading] = useState(true);

  // Date helpers
  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  const getMonthStartDate = () => {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    return firstDay.toISOString().split('T')[0];
  };

  const getYearStartDate = () => {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), 0, 1);
    return firstDay.toISOString().split('T')[0];
  };

  // Calculate totals from data
  const calculateTotals = (data, dateField = 'date') => {
    if (!Array.isArray(data) || data.length === 0) {
      return { total: 0, todayTotal: 0, monthTotal: 0 };
    }
    
    const today = getTodayDate();
    const monthStart = getMonthStartDate();
    
    let total = 0;
    let todayTotal = 0;
    let monthTotal = 0;

    data.forEach(item => {
      const itemDate = item[dateField]?.split(' ')[0] || item[dateField];
      const amount = parseFloat(item.total_amount || item.grand_total || item.amount || 0) || 0;
      
      total += amount;
      
      if (itemDate === today) {
        todayTotal += amount;
      }
      
      if (itemDate >= monthStart) {
        monthTotal += amount;
      }
    });

    return { total, todayTotal, monthTotal };
  };

  // Fetch all dashboard data
  useEffect(() => {
    const fetchAllData = async () => {
      setLoading(true);
      try {
        // Fetch stock data
        const stockPromises = [
          axios.get(`${GlobalService.path}/goodsStock`),
          axios.get(`${GlobalService.path}/retailStock`),
          axios.get(`${GlobalService.path}/rawStock`)
        ];

        const stockResults = await Promise.all(stockPromises);
        setTableData(stockResults[0].data.data || []);
        setTableData1(stockResults[1].data.data || []);
        setTableData2(stockResults[2].data.data || []);
        setStockLoading(false);

        // Fetch sales data
        const salesRes = await axios.get(`${GlobalService.path}/fetchSale`);
        const salesData = salesRes.data.data || [];
        const salesTotals = calculateTotals(salesData, 'date');
        setTotalSales(salesTotals.total);
        setTodaySales(salesTotals.todayTotal);
        setMonthSales(salesTotals.monthTotal);
        
        // Get recent invoices (last 5)
        const sortedSales = [...salesData]
          .sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0))
          .slice(0, 5);
        setRecentInvoices(sortedSales);

        // Fetch purchase data
        const purchaseRes = await axios.get(`${GlobalService.path}/fetchPurchase`);
        const purchaseData = purchaseRes.data.data || [];
        const purchaseTotals = calculateTotals(purchaseData, 'date');
        setTotalPurchases(purchaseTotals.total);
        setTodayPurchases(purchaseTotals.todayTotal);
        setMonthPurchases(purchaseTotals.monthTotal);

        // Fetch customer count
        const customerRes = await axios.get(`${GlobalService.path}/fetchCustomer`);
        setTotalCustomers(customerRes.data.data?.length || 0);

        // Fetch supplier count
        const supplierRes = await axios.get(`${GlobalService.path}/fetchSupplier`);
        setTotalSuppliers(supplierRes.data.data?.length || 0);

        // Fetch pending amounts
        try {
          const pendingRes = await axios.get(`${GlobalService.path}/customerPendingAmount`);
          const pendingData = pendingRes.data.data || [];
          const totalPending = pendingData.reduce((sum, item) => {
            return sum + (parseFloat(item.pendingAmount || 0) || 0);
          }, 0);
          setTotalPendingAmount(totalPending);
        } catch (error) {
          console.error("Error fetching pending amounts:", error);
          setTotalPendingAmount(0);
        }

      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
  }, []);
  

  return (
    <>
      {/* Page Container */}
      <div className="page-container">
        {/* Page Sidebar */}
        <SideBar />
        {/* /Page Sidebar */}
        {/* Page Content */}
        <div className="page-content">
          {/* Page Header */}
          <Header />
          {/* /Page Header */}
          {/* Page Inner */}
          <div className="page-inner">
            <div className="page-title">
              <h3 className="breadcrumb-header">Dashboard</h3>
            </div>
            <div id="main-wrapper">
              {loading ? (
                <div className="text-center" style={{ padding: "50px" }}>
                  <p>Loading dashboard data...</p>
                </div>
              ) : (
                <>
                  {/* Statistics Cards Row */}
                  <div className="row">
                    <div className="col-lg-3 col-md-6">
                      <div className="panel panel-white stats-widget">
                        <div className="panel-body">
                          <div className="pull-left">
                            <span className="stats-number">
                              ₹{totalSales.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                            </span>
                            <p className="stats-info">Total Sales</p>
                            <small style={{ color: '#666' }}>
                              Today: ₹{todaySales.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                            </small>
                          </div>
                          <div className="pull-right">
                            <i className="icon-arrow_upward stats-icon" style={{ color: '#4caf50' }} />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-3 col-md-6">
                      <div className="panel panel-white stats-widget">
                        <div className="panel-body">
                          <div className="pull-left">
                            <span className="stats-number">
                              ₹{totalPurchases.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                            </span>
                            <p className="stats-info">Total Purchases</p>
                            <small style={{ color: '#666' }}>
                              Today: ₹{todayPurchases.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                            </small>
                          </div>
                          <div className="pull-right">
                            <i className="icon-arrow_downward stats-icon" style={{ color: '#f44336' }} />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-3 col-md-6">
                      <div className="panel panel-white stats-widget">
                        <div className="panel-body">
                          <div className="pull-left">
                            <span className="stats-number">{totalCustomers}</span>
                            <p className="stats-info">Total Customers</p>
                            <small style={{ color: '#666' }}>Active customers</small>
                          </div>
                          <div className="pull-right">
                            <i className="icon-arrow_upward stats-icon" style={{ color: '#2196f3' }} />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-3 col-md-6">
                      <div className="panel panel-white stats-widget">
                        <div className="panel-body">
                          <div className="pull-left">
                            <span className="stats-number">{totalSuppliers}</span>
                            <p className="stats-info">Total Suppliers</p>
                            <small style={{ color: '#666' }}>Active suppliers</small>
                          </div>
                          <div className="pull-right">
                            <i className="icon-arrow_upward stats-icon" style={{ color: '#ff9800' }} />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Additional Statistics Row */}
                  <div className="row">
                    <div className="col-lg-3 col-md-6">
                      <div className="panel panel-white stats-widget">
                        <div className="panel-body">
                          <div className="pull-left">
                            <span className="stats-number">
                              ₹{totalPendingAmount.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                            </span>
                            <p className="stats-info">Pending Amount</p>
                            <small style={{ color: '#666' }}>Customer pending</small>
                          </div>
                          <div className="pull-right">
                            <i className="icon-arrow_upward stats-icon" style={{ color: '#ff5722' }} />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-3 col-md-6">
                      <div className="panel panel-white stats-widget">
                        <div className="panel-body">
                          <div className="pull-left">
                            <span className="stats-number">
                              ₹{monthSales.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                            </span>
                            <p className="stats-info">This Month Sales</p>
                            <small style={{ color: '#666' }}>Current month</small>
                          </div>
                          <div className="pull-right">
                            <i className="icon-arrow_upward stats-icon" style={{ color: '#4caf50' }} />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-3 col-md-6">
                      <div className="panel panel-white stats-widget">
                        <div className="panel-body">
                          <div className="pull-left">
                            <span className="stats-number">
                              ₹{monthPurchases.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                            </span>
                            <p className="stats-info">This Month Purchases</p>
                            <small style={{ color: '#666' }}>Current month</small>
                          </div>
                          <div className="pull-right">
                            <i className="icon-arrow_downward stats-icon" style={{ color: '#f44336' }} />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-3 col-md-6">
                      <div className="panel panel-white stats-widget">
                        <div className="panel-body">
                          <div className="pull-left">
                            <span className="stats-number">
                              ₹{(totalSales - totalPurchases).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                            </span>
                            <p className="stats-info">Net Profit</p>
                            <small style={{ color: '#666' }}>Sales - Purchases</small>
                          </div>
                          <div className="pull-right">
                            <i className="icon-arrow_upward stats-icon" style={{ 
                              color: (totalSales - totalPurchases) >= 0 ? '#4caf50' : '#f44336' 
                            }} />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* Stock Tables Row */}
              <div className="row">
                {/* Goods Stock */}
                <div className="col-lg-4 col-md-6">
                  <div className="panel panel-white">
                    <div className="panel-heading clearfix">
                      <h4 className="panel-title">Goods Stock</h4>
                    </div>
                    <div className="table-container">
                      {stockLoading ? (
                        <div className="text-center" style={{ padding: "20px" }}>
                          <p>Loading stock data...</p>
                        </div>
                      ) : (
                        <table className="display" style={{width:"100%"}}>
                          <thead>
                            <tr>
                              <th>Sr. No.</th>
                              <th>Product Name</th>
                              <th>Available Stock (Kg)</th>
                            </tr>
                          </thead>
                          <tbody>
                            {Array.isArray(tableData) && tableData.length > 0 ? (
                              tableData.map((row, index) => {
                                const availableStock = (parseFloat(row.TotalProductionWeight || 0)) - 
                                  ((parseFloat(row.TotalDamageWeight || 0)) + (parseFloat(row.TotalSalesWeight || 0)));
                                return (
                                  <tr key={index}>
                                    <td>{index + 1}</td>
                                    <td>{row.prod_name || 'N/A'}</td>
                                    <td>{availableStock.toFixed(2)} Kg</td>
                                  </tr>
                                );
                              })
                            ) : (
                              <tr>
                                <td colSpan="3" className="text-center">No stock data available</td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      )}
                    </div>
                  </div>
                </div>
                {/* Retail Stock */}
                <div className="col-lg-4 col-md-6">
                  <div className="panel panel-white">
                    <div className="panel-heading clearfix">
                      <h4 className="panel-title">Retail Stock</h4>
                    </div>
                    <div className="table-container">
                      {stockLoading ? (
                        <div className="text-center" style={{ padding: "20px" }}>
                          <p>Loading stock data...</p>
                        </div>
                      ) : (
                        <table className="display" style={{width:"100%"}}>
                          <thead>
                            <tr>
                              <th>Sr. No.</th>
                              <th>Product Name</th>
                              <th>Available Stock (Kg)</th>
                            </tr>
                          </thead>
                          <tbody>
                            {Array.isArray(tableData1) && tableData1.length > 0 ? (
                              tableData1.map((row, index) => {
                                const availableStock = (parseFloat(row.TotalPurchaseWeight || 0)) - 
                                  ((parseFloat(row.TotalDamageWeight || 0)) + (parseFloat(row.TotalSalesWeight || 0)));
                                return (
                                  <tr key={index}>
                                    <td>{index + 1}</td>
                                    <td>{row.prod_name || 'N/A'}</td>
                                    <td>{availableStock.toFixed(2)} Kg</td>
                                  </tr>
                                );
                              })
                            ) : (
                              <tr>
                                <td colSpan="3" className="text-center">No stock data available</td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      )}
                    </div>
                  </div>
                </div>
                {/* Raw Stock */}
                <div className="col-lg-4 col-md-6">
                  <div className="panel panel-white">
                    <div className="panel-heading clearfix">
                      <h4 className="panel-title">Raw Stock</h4>
                    </div>
                    <div className="table-container">
                      {stockLoading ? (
                        <div className="text-center" style={{ padding: "20px" }}>
                          <p>Loading stock data...</p>
                        </div>
                      ) : (
                        <table className="display" style={{width:"100%"}}>
                          <thead>
                            <tr>
                              <th>Sr. No.</th>
                              <th>Product Name</th>
                              <th>Available Stock (Kg)</th>
                            </tr>
                          </thead>
                          <tbody>
                            {Array.isArray(tableData2) && tableData2.length > 0 ? (
                              tableData2.map((row, index) => {
                                const availableStock = (parseFloat(row.TotalPurchaseWeight || 0)) - 
                                  ((parseFloat(row.TotalDamageWeight || 0)) + (parseFloat(row.TotalUsageWeight || 0)));
                                return (
                                  <tr key={index}>
                                    <td>{index + 1}</td>
                                    <td>{row.prod_name || 'N/A'}</td>
                                    <td>{availableStock.toFixed(2)} Kg</td>
                                  </tr>
                                );
                              })
                            ) : (
                              <tr>
                                <td colSpan="3" className="text-center">No stock data available</td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              {/* Recent Invoices and Quick Stats Row */}
              <div className="row">
                <div className="col-lg-8 col-md-12">
                  <div className="panel panel-white">
                    <div className="panel-heading clearfix">
                      <h4 className="panel-title">Recent Sales Invoices</h4>
                    </div>
                    <div className="panel-body">
                      {loading ? (
                        <div className="text-center" style={{ padding: "20px" }}>
                          <p>Loading invoices...</p>
                        </div>
                      ) : (
                        <div className="table-responsive invoice-table">
                          <table className="table">
                            <thead>
                              <tr>
                                <th>Invoice No.</th>
                                <th>Date</th>
                                <th>Customer</th>
                                <th>Amount</th>
                                <th>Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {recentInvoices.length > 0 ? (
                                recentInvoices.map((invoice, index) => {
                                  const invoiceDate = invoice.date ? invoice.date.split(' ')[0] : 'N/A';
                                  const amount = parseFloat(invoice.total_amount || invoice.grand_total || 0);
                                  return (
                                    <tr key={index}>
                                      <th scope="row">{invoice.invoice_no || 'N/A'}</th>
                                      <td>{invoiceDate}</td>
                                      <td>{invoice.cust_name || invoice.customer_name || 'N/A'}</td>
                                      <td>₹{amount.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</td>
                                      <td>
                                        <span className="label label-success">
                                          Completed
                                        </span>
                                      </td>
                                    </tr>
                                  );
                                })
                              ) : (
                                <tr>
                                  <td colSpan="5" className="text-center">No recent invoices found</td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="col-lg-4 col-md-12">
                  <div className="panel panel-white">
                    <div className="panel-heading clearfix">
                      <h4 className="panel-title">Quick Summary</h4>
                    </div>
                    <div className="panel-body">
                      <div className="container-fluid">
                        <div className="server-load row">
                          <div className="server-stat col-sm-6" style={{ marginBottom: '15px' }}>
                            <p style={{ fontSize: '24px', fontWeight: 'bold', color: '#4caf50' }}>
                              ₹{todaySales.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                            </p>
                            <span>Today's Sales</span>
                          </div>
                          <div className="server-stat col-sm-6" style={{ marginBottom: '15px' }}>
                            <p style={{ fontSize: '24px', fontWeight: 'bold', color: '#f44336' }}>
                              ₹{todayPurchases.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                            </p>
                            <span>Today's Purchases</span>
                          </div>
                          <div className="server-stat col-sm-6" style={{ marginBottom: '15px' }}>
                            <p style={{ fontSize: '24px', fontWeight: 'bold', color: '#2196f3' }}>
                              {totalCustomers}
                            </p>
                            <span>Total Customers</span>
                          </div>
                          <div className="server-stat col-sm-6" style={{ marginBottom: '15px' }}>
                            <p style={{ fontSize: '24px', fontWeight: 'bold', color: '#ff9800' }}>
                              {totalSuppliers}
                            </p>
                            <span>Total Suppliers</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* Business Summary Row */}
              <div className="row">
                <div className="col-lg-6 col-md-12">
                  <div className="panel panel-white">
                    <div className="panel-heading clearfix">
                      <h4 className="panel-title">Business Overview</h4>
                    </div>
                    <div className="panel-body">
                      <div className="business-summary">
                        <div className="summary-item" style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f5f5f5', borderRadius: '5px' }}>
                          <h5 style={{ marginBottom: '10px', color: '#333' }}>Financial Summary</h5>
                          <div className="row">
                            <div className="col-sm-6">
                              <p style={{ margin: '5px 0' }}>
                                <strong>Total Sales:</strong> ₹{totalSales.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                              </p>
                              <p style={{ margin: '5px 0' }}>
                                <strong>Total Purchases:</strong> ₹{totalPurchases.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                              </p>
                            </div>
                            <div className="col-sm-6">
                              <p style={{ margin: '5px 0' }}>
                                <strong>Net Profit:</strong> 
                                <span style={{ 
                                  color: (totalSales - totalPurchases) >= 0 ? '#4caf50' : '#f44336',
                                  fontWeight: 'bold'
                                }}>
                                  {' '}₹{(totalSales - totalPurchases).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                                </span>
                              </p>
                              <p style={{ margin: '5px 0' }}>
                                <strong>Pending Amount:</strong> ₹{totalPendingAmount.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                              </p>
                            </div>
                          </div>
                        </div>
                        <div className="summary-item" style={{ padding: '15px', backgroundColor: '#f5f5f5', borderRadius: '5px' }}>
                          <h5 style={{ marginBottom: '10px', color: '#333' }}>Monthly Performance</h5>
                          <div className="row">
                            <div className="col-sm-6">
                              <p style={{ margin: '5px 0' }}>
                                <strong>Month Sales:</strong> ₹{monthSales.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                              </p>
                            </div>
                            <div className="col-sm-6">
                              <p style={{ margin: '5px 0' }}>
                                <strong>Month Purchases:</strong> ₹{monthPurchases.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-lg-6 col-md-12">
                  <div className="panel panel-white">
                    <div className="panel-heading clearfix">
                      <h4 className="panel-title">Stock Summary</h4>
                    </div>
                    <div className="panel-body">
                      <div className="stock-summary">
                        <div className="summary-item" style={{ marginBottom: '15px', padding: '15px', backgroundColor: '#e3f2fd', borderRadius: '5px' }}>
                          <h5 style={{ marginBottom: '10px', color: '#1976d2' }}>Goods Stock</h5>
                          <p style={{ margin: '5px 0', fontSize: '18px', fontWeight: 'bold' }}>
                            {Array.isArray(tableData) ? tableData.length : 0} Products
                          </p>
                        </div>
                        <div className="summary-item" style={{ marginBottom: '15px', padding: '15px', backgroundColor: '#fff3e0', borderRadius: '5px' }}>
                          <h5 style={{ marginBottom: '10px', color: '#f57c00' }}>Retail Stock</h5>
                          <p style={{ margin: '5px 0', fontSize: '18px', fontWeight: 'bold' }}>
                            {Array.isArray(tableData1) ? tableData1.length : 0} Products
                          </p>
                        </div>
                        <div className="summary-item" style={{ padding: '15px', backgroundColor: '#f3e5f5', borderRadius: '5px' }}>
                          <h5 style={{ marginBottom: '10px', color: '#7b1fa2' }}>Raw Stock</h5>
                          <p style={{ margin: '5px 0', fontSize: '18px', fontWeight: 'bold' }}>
                            {Array.isArray(tableData2) ? tableData2.length : 0} Products
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* Row */}

              {/* Row */}
            </div>
            {/* Main Wrapper */}
            <div className="page-footer">
              <p>
                Made with <i className="fa fa-heart" /> by stacks
              </p>
            </div>
          </div>
          {/* /Page Inner */}
        </div>
        {/* /Page Content */}
      </div>
      {/* /Page Container */}
    </>
  );
};
